<template>
  <main>
    <b-card no-body>
      <!-- ? Template Name and Detail Start -->
      <section name="template-name-and-detail" class="card-padding">
        <div
          name="config-name-and-tags__wrapper"
          class="d-flex align-items-center mb-1"
        >
          <span class="card__header--topic"
            >Template: <b>{{ templateDetail.templateName }}</b>
          </span>
          <div id="config-tag-popover">
            <feather-icon icon="TagIcon" size="14" class="tag-icon--style" />
          </div>
          <b-popover
            ref="popover"
            target="config-tag-popover"
            triggers="hover"
            placement="right"
            container="my-container"
            boundary="window"
          >
            <template v-slot:title>
              <div class="d-flex justify-content-between align-items-left">
                <span class="text--normal-size">Tag</span>
              </div>
            </template>
            <div class="badge__container">
              <b-badge
                v-for="(tag, index) in templateDetail.tags"
                :key="index"
                variant="light-primary"
                class="badge--style tag--space"
              >
                {{ tag }}
              </b-badge>
            </div>
          </b-popover>
        </div>
        <span>{{ templateDetail.description }}</span>
      </section>
      <!-- ? Template Name and Detail End -->
      <hr />
      <!-- ? Template Parameters Start -->
      <section name="template-parameter" class="card-padding">
        <div name="template-parameters--topic" class="mb-1">
          <span class="card__header--topic">Template Parameters</span>
        </div>
        <div
          v-for="(param, index) in templateDetail.parameters"
          :key="index"
          class="mb-1 custom-max-width"
        >
          <atom-input
            v-model="param.value"
            :title="param.parameterName"
            :placeholder="param.parameterName"
            :isAsterisk="true"
            :isError="isValidateTrigger ? isEmptyString(param.value) : false"
          ></atom-input>
        </div>
      </section>
      <!-- ? Template Parameters End -->
      <!-- ? Buttons Section Start -->
      <section
        name="button-section"
        class="card-padding d-flex justify-content-end"
      >
        <atom-button
          buttonId="generate-config-button"
          :isDisable="isErrorOccur"
          @click="onClickGenerateConfigTemplateButton"
        >
          Generate Configuration
        </atom-button>

        <atom-button
          class="ml-1"
          buttonId="cancel-generate-config-button"
          buttonStyle="border"
          buttonColor="secondary"
          @click="onClickCancelGenerateEditConfigTemplate"
        >
          Cancel
        </atom-button>
      </section>
      <!-- ? Buttons Section End -->
    </b-card>
    <!-- ? Modal Section Start -->
    <molecule-modal-dialog
      modalId="modal-generated-configuration"
      textTitle="Generated Configuration"
      textSubtitle="Please check the configuration detail below."
      :isButtonLeft="false"
      :isButtonRight="false"
      :isSlotBody="true"
      :isCloseOnBackDrop="false"
      @clickButtonRight="onClickCloseFromModal"
    >
      <template #body>
        <div class="mt-1 w-95">
          <div
            name="topic-and-button-wrapper"
            class="d-flex justify-content-between align-items-center mb-2"
          >
            <span class="modal-topic--text">Generated Configuration</span>
            <atom-button
              buttonId="copy-generated-config"
              buttonStyle="border"
              @click="onClickCopyGeneratedConfig"
            >
              <span class="button-text">Copy to Clipboard</span>
            </atom-button>
          </div>
          <div name="generated-config__wrapper">
            <prism-editor
              v-model="generateConfig"
              class="text-area-with-line-number custom-max-height"
              readonly
              :lineNumbers="screenWidth > 1080 ? true : false"
              :highlight="highlighter"
            ></prism-editor>
          </div>
        </div>
      </template>
    </molecule-modal-dialog>
    <!-- ? Modal Section End -->
    <textarea
      v-on:focus="$event.target.select()"
      readonly
      ref="copyGenerateConfigTextBox"
      class="hidden-textbox"
      :value="generateConfig"
    />
  </main>
</template>

<script>
// import Prism Editor
import { PrismEditor } from "vue-prism-editor"
import "vue-prism-editor/dist/prismeditor.min.css"

// import highlighting library (you can use any library you want just return html string)
import { highlight, languages } from "prismjs/components/prism-core"
import "prismjs/components/prism-clike"
import "prismjs/components/prism-javascript"
import "prismjs/themes/prism-tomorrow.css" // import syntax highlighting styles

import { BBadge, BCard, BPopover } from "bootstrap-vue"
import AtomButton from "@/components/atoms/AtomButton.vue"
import AtomInput from "@/components/atoms/AtomInput.vue"
import AtomTextArea from "@/components/atoms/AtomTextArea.vue"
import MoleculeModalDialog from "@/components/molecules/MoleculeModalDialog.vue"
import VueScreenSize from "vue-screen-size"
import mixins from "@/mixins"
import mixinsValidator from '@/mixins/mixinsValidator'
export default {
  name: "OrganismUseConfigTemplate",
  mixins: [mixins, mixinsValidator, VueScreenSize.VueScreenSizeMixin],
  components: {
    AtomButton,
    AtomInput,
    AtomTextArea,
    BBadge,
    BCard,
    BPopover,
    MoleculeModalDialog,
    PrismEditor,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    templateDetail: {
      type: Object,
      required: true,
    },
    generateConfig: {
      type: String,
      required: false,
      default: "",
    },
  },
  emits: ["clickGenerate"],
  data() {
    return {
      modalId: null,
      isValidateTrigger: false,
    }
  },
  computed: {
    screenWidth() {
      return this.$vssWidth
    },
    isErrorOccur() {
      return this.isValidateTrigger
        ? this.templateDetail.parameters.some((param) => {
            return this.isEmptyString(param.value)
          })
        : false
    },
  },
  methods: {
    highlighter(code) {
      return code // languages.<insert language> to return html with markup
    },

    navigateTo(path) {
      this.$router.push(path)
    },

    onClickGenerateConfigTemplateButton() {
      this.isValidateTrigger = true
      this.$nextTick(() => {
        if (!this.isErrorOccur) {
          const configTemplateParameters = []
          this.templateDetail.parameters.forEach((param) => {
            configTemplateParameters.push({
              key: param.parameterName,
              value: param.value,
            })
          })
          this.$emit("clickGenerate", configTemplateParameters)
          this.openModal("modal-generated-configuration")
          this.isValidateTrigger = false
        }
      })
    },

    onClickCancelGenerateEditConfigTemplate() {
      this.navigateTo(
        `/network-configuration/configuration-template/${this.id}`
      )
    },

    onClickCloseFromModal() {
      this.closeModal()
    },

    onClickCopyGeneratedConfig() {
      this.$refs.copyGenerateConfigTextBox.focus()
      document.execCommand("copy")
      this.toastSuccess("Copy successfully")
    },

    // modal
    openModal(id) {
      this.modalId = id
      this.$bvModal.show(this.modalId)
    },
    closeModal() {
      this.$bvModal.hide(this.modalId)
      this.modalId = null
    },
  },
}
</script>

<style lang="scss" scoped>
/* required class */
.my-editor {
  /* we don't use `language-` classes anymore so thats why we need to add background and text color manually */
  background: #f5f7fa;

  /* you must provide font-family font-size line-height. Example: */
  font-family: Fira code, Fira Mono, Consolas, Menlo, Courier, monospace;
  font-size: 14px;
  line-height: 1.5;
  padding: 5px;
  border-radius: 5px;
}

/* optional class for removing the outline */
.prism-editor__textarea:focus {
  outline: none;
}

hr {
  margin-top: 1.5rem;
  margin-bottom: 0;
  border: 0;
  border-top: 1px solid #ebe9f1;
}

.card-padding {
  padding: 21px;
}

.card-padding-x {
  padding-left: 21px;
  padding-right: 21px;
}

.card-padding-y {
  padding-top: 21px;
  padding-bottom: 21px;
}

.card-padding-top {
  padding-top: 21px;
}

.card-padding-bottom {
  padding-bottom: 21px;
}

.card__header--topic {
  font-weight: 500;
  font-size: 18px;
}

.text--normal-size {
  font-size: 12px;
}

.tag-icon--style {
  color: #9d95f5;
  margin-left: 10px;
}

.badge--style {
  max-width: 30ch;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tag--space {
  margin-left: 4px;
  margin-bottom: 4px;
}

.modal-topic--text {
  font-weight: 500;
  font-size: 18px;
  line-height: 22px;
}

.custom-max-height {
  max-height: 500px;
  overflow-y: auto;
}

.hidden-textbox {
  position: absolute;
  top: -99999px;
}

// Small devices (landscape phones, 576px and up)
@media screen and (min-width: 576px) {
}

// Medium devices (tablets, 768px and up)
@media screen and (min-width: 768px) {
  .custom-max-width {
    max-width: 60%;
  }
}

// Large devices (desktops, 992px and up)
@media screen and (min-width: 992px) {
}
</style>
